/**
 * address_fields_controller.js — bind an address autocomplete to a plain form.
 *
 * Hosts on a <form> that carries an address input plus the hidden fields a
 * suggestion fills. Picking a suggestion writes street / city / state /
 * postcode / latitude / longitude into the form by NAME, which is the same
 * contract SiteService.update accepts, so the form posts normally and the
 * server stores the parts without re-geocoding what the user already resolved.
 *
 * Supplying coordinates matters: SiteService._maybe_geocode only calls out to
 * the geocoder when they are absent, so a suggestion picked here saves a
 * network round trip and a chance of the geocoder disagreeing with the address
 * the user actually chose.
 *
 * Editing the text by hand clears those parts again, so a form never posts the
 * parts of one address beside the text of another; the server then geocodes
 * what was typed. The autocomplete sets the input's value from code on a pick,
 * which fires no input event, so a pick is not undone by this.
 *
 * window.AddressAutocomplete is a global loaded by base.html (address_autocomplete.js,
 * defer) and Stimulus starts on DOMContentLoaded, so it is always defined by
 * the time this connects. A page on public_base.html loads that script itself.
 *
 * Wired with data-controller="address-fields" on the form, and
 * data-address-autocomplete on the input to bind. The optional
 * data-address-fields-country-value (ISO-2 region code) biases suggestions on
 * pages with no business session, where window.BUSINESS_REGION is not set.
 */

import { Controller } from "@hotwired/stimulus";

// The parts of a suggestion that land in the form. Names, not ids: the same
// form partial is rendered per site, so ids would have to be uniquified while
// names stay constant.
const SUGGESTION_FIELDS = [
    "street",
    "city",
    "state",
    "postcode",
    "latitude",
    "longitude",
];

export default class extends Controller {
    static values = { country: String };

    connect() {
        const input = this.element.querySelector("[data-address-autocomplete]");
        if (!input || typeof window.AddressAutocomplete === "undefined") return;

        // Without a country value the class biases to window.BUSINESS_REGION,
        // which is the business's own region and the right answer in the app.
        const options = { onSelect: (suggestion) => this.fill(suggestion) };
        if (this.hasCountryValue) options.country = this.countryValue;
        this.autocomplete = new window.AddressAutocomplete(input, options);

        this.input = input;
        this._onInput = () => this.fill({});
        input.addEventListener("input", this._onInput);
    }

    disconnect() {
        // destroy() removes the dropdown and the document-level pointerdown
        // listener. Turbo replaces the body on navigation and this card is a
        // Turbo Stream target besides, so an instance left behind here is a
        // leak on every visit and every save.
        if (this.autocomplete) this.autocomplete.destroy();
        this.autocomplete = null;
        if (this.input) this.input.removeEventListener("input", this._onInput);
        this.input = null;
    }

    fill(suggestion) {
        for (const name of SUGGESTION_FIELDS) {
            const field = this.element.elements[name];
            if (field) field.value = suggestion[name] || "";
        }
    }
}
